import { ArrowLeftIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react'
import { ApiErrorText } from '@/components/UpgradePrompt'
import { useMemo } from 'react'
import { Link, useParams } from 'react-router'

import { useMe, useSetZoneMember, useUsers, useZoneMembers, useZones } from '@/api/queries'
import { PageBody, PageHeader } from '@/components/AppShell'
import { LifecycleBadge, RoleBadge } from '@/components/badges'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute, formatRelative } from '@/lib/format'
import { useReadOnly } from '@/staff/hooks'
import { zoneGrantCandidates } from './invite-zones'

/**
 * Who may reach one zone (§16).
 *
 * An ADMIN reaches every zone in the account unconditionally, so granting one a
 * `zone_members` row would be meaningless — their rows are shown as implicit
 * and cannot be toggled. Only USERs are actually grantable.
 */
export function ZoneMembersPage() {
  const params = useParams()
  const zoneId = params['zoneId']

  const me = useMe()
  const readOnly = useReadOnly()
  const isAdmin = me.data?.role === 'ADMIN'
  const zones = useZones('all')
  const members = useZoneMembers(zoneId, isAdmin)
  const users = useUsers(isAdmin)
  const setMember = useSetZoneMember()

  const zone = zones.data?.find((entry) => entry.id === zoneId)

  const grantedIds = useMemo(
    () => new Set((members.data ?? []).map((member) => member.user_id)),
    [members.data],
  )

  if (me.isSuccess && !isAdmin) {
    return (
      <>
        <PageHeader eyebrow="Zone access" title="Zone access" />
        <PageBody>
          <EmptyState
            icon={ShieldCheckIcon}
            title="ADMIN only"
            description="Granting and revoking access to a zone is an ADMIN operation. Ask an ADMIN on this account if you need someone added."
          />
        </PageBody>
      </>
    )
  }

  // A BILLING member has no zone access (BI5): never offered a grant the server refuses.
  const rows = zoneGrantCandidates(users.data ?? [])
  // Both queries are disabled until `me` resolves, and a disabled react-query v5
  // query reports isLoading === false — so isLoading alone flashes a confident
  // "no users to grant" before the real list has even been asked for.
  const loadingRows = me.isPending || users.isPending || members.isPending

  return (
    <>
      <PageHeader
        eyebrow="Zone access"
        title={zone === undefined ? 'Zone access' : `Access to ${zone.name}`}
        description="Which users of this account may reach this zone. Revoking access does not touch anything the user already sent — messages are permanent and keep the name that sent them."
        actions={
          <Button asChild variant="outline">
            <Link to="/app/zones">
              <ArrowLeftIcon className="size-4" />
              All zones
            </Link>
          </Button>
        }
      />

      <PageBody>
        {zone === undefined ? null : (
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-sm text-foreground">{zone.name}</span>
            <LifecycleBadge state={zone.zone_state} />
            <span className="font-mono text-xs text-muted-foreground">{zone.public_id}</span>
          </div>
        )}

        {setMember.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not change that grant</AlertTitle>
            <AlertDescription><ApiErrorText error={setMember.error} /></AlertDescription>
          </Alert>
        ) : null}

        {users.isError || members.isError ? (
          <ErrorState
            error={users.error ?? members.error}
            title="Could not load zone access"
            onRetry={() => {
              void users.refetch()
              void members.refetch()
            }}
          />
        ) : loadingRows ? (
          <Card className="overflow-hidden">
            <TableSkeleton rows={4} columns={4} />
          </Card>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title="No users to grant"
            description={
              <>
                This account has no other users yet. Invite one from the{' '}
                <Link to="/app/users" className="text-primary underline-offset-4 hover:underline">
                  Users
                </Link>{' '}
                page, then grant them this zone.
              </>
            }
          />
        ) : (
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Access</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((user) => {
                  const granted = grantedIds.has(user.id)
                  const implicit = user.role === 'ADMIN'
                  const pending =
                    setMember.isPending && setMember.variables?.userId === user.id
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <span className="block text-sm font-medium text-foreground">
                          {user.name}
                        </span>
                        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      </TableCell>
                      <TableCell>
                        <RoleBadge role={user.role} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {implicit
                          ? 'Every zone in the account'
                          : granted
                            ? 'Granted'
                            : 'No access'}
                      </TableCell>
                      <TableCell className="text-right">
                        {implicit ? (
                          <span className="text-xs text-muted-foreground">implicit</span>
                        ) : readOnly ? null : (
                          <Button
                            variant={granted ? 'ghost' : 'outline'}
                            size="sm"
                            disabled={pending || zoneId === undefined}
                            className={granted ? 'text-muted-foreground hover:text-destructive' : undefined}
                            onClick={() => {
                              if (zoneId === undefined) return
                              setMember.mutate({ zoneId, userId: user.id, granted: !granted })
                            }}
                          >
                            {pending ? 'Saving…' : granted ? 'Revoke' : 'Grant access'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        {members.data === undefined || members.data.length === 0 ? null : (
          <p className="text-xs text-muted-foreground">
            Earliest grant:{' '}
            <span title={formatAbsolute(members.data[0]?.granted_at)}>
              {formatRelative(members.data[0]?.granted_at)}
            </span>
            .
          </p>
        )}
      </PageBody>
    </>
  )
}
